import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Tag, Plus, Trash2, Save, Loader2, ChevronLeft, Info, GripVertical,
  ArrowUp, ArrowDown, Star, AlertTriangle,
} from 'lucide-react';
import useApiMethods from '../../../lib/useApiMethods';
import ToastMsg from '../../../components/common/ToastMsg';
import { cn } from '../../../utils/tailwindUtils';
import { ENGAGE_STATUS_LABEL, ENGAGE_STATUS_ORDER } from '../../../utils/engageStageBadge';

const STAGE_KINDS = [
  { value: 'open', label: 'Open' },
  { value: 'won',  label: 'Won / Converted' },
  { value: 'lost', label: 'Lost / Dropped' },
];

const KIND_BADGE = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  won:  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  lost: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const blankStage = () => ({
  _key: `new-${Math.random().toString(36).slice(2, 9)}`,
  name: '', stage_kind: 'open', maps_to_status: 'new',
  is_default: false, colour: '', is_active: true,
});

const blankType = () => ({
  _key: `new-${Math.random().toString(36).slice(2, 9)}`,
  _new: true,
  name: '', code: '', description: '', colour: '', order: 0, is_active: true,
  stages: [{ ...blankStage(), name: 'Enquiry Received', is_default: true }],
});

export default function PatientTypesSettings() {
  const navigate = useNavigate();
  const { fetch: apiFetch, create: apiCreate, update: apiUpdate, remove: apiRemove } = useApiMethods({ throwOnFail: true });

  const [types, setTypes]       = useState(null);   // null = loading
  const [selected, setSelected] = useState(null);   // index into types
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);
  const [dirty, setDirty]       = useState(false);

  const load = async (keepIndex = 0) => {
    try {
      const resp = await apiFetch('engage/patient-types/', { include_inactive: 1 });
      const list = resp?.results || resp?.data?.results || [];
      const rows = (Array.isArray(list) ? list : []).map(t => ({
        ...t,
        _key: `t-${t.id}`,
        stages: (t.stages || []).map(s => ({ ...s, _key: `s-${s.id}` })),
      }));
      setTypes(rows);
      setSelected(rows.length ? Math.min(keepIndex, rows.length - 1) : null);
      setDirty(false);
      setError(null);
    } catch {
      setError('Failed to load patient types');
      setTypes([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const current = types && selected !== null ? types[selected] : null;

  // ── type-level edits ───────────────────────────────────────────────────────
  const patchType = (patch) => {
    setTypes(ts => ts.map((t, i) => (i === selected ? { ...t, ...patch } : t)));
    setDirty(true);
  };

  const addType = () => {
    setTypes(ts => {
      const next = [...(ts || []), { ...blankType(), order: (ts || []).length + 1 }];
      setSelected(next.length - 1);
      return next;
    });
    setDirty(true);
  };

  // ── stage-level edits ──────────────────────────────────────────────────────
  const patchStage = (idx, patch) => {
    setTypes(ts => ts.map((t, i) => {
      if (i !== selected) return t;
      return { ...t, stages: t.stages.map((s, j) => (j === idx ? { ...s, ...patch } : s)) };
    }));
    setDirty(true);
  };

  const addStage = () => {
    setTypes(ts => ts.map((t, i) => (i === selected ? { ...t, stages: [...t.stages, blankStage()] } : t)));
    setDirty(true);
  };

  const removeStage = (idx) => {
    const stage = current.stages[idx];
    if (stage.enquiry_count > 0) {
      ToastMsg('error', `"${stage.name}" has ${stage.enquiry_count} enquiry(s) on it. Move them first.`);
      return;
    }
    setTypes(ts => ts.map((t, i) => (i === selected ? { ...t, stages: t.stages.filter((_, j) => j !== idx) } : t)));
    setDirty(true);
  };

  const moveStage = (idx, delta) => {
    const to = idx + delta;
    if (!current || to < 0 || to >= current.stages.length) return;
    setTypes(ts => ts.map((t, i) => {
      if (i !== selected) return t;
      const next = [...t.stages];
      [next[idx], next[to]] = [next[to], next[idx]];
      return { ...t, stages: next };
    }));
    setDirty(true);
  };

  const setDefaultStage = (idx) => {
    setTypes(ts => ts.map((t, i) => {
      if (i !== selected) return t;
      return { ...t, stages: t.stages.map((s, j) => ({ ...s, is_default: j === idx })) };
    }));
    setDirty(true);
  };

  // ── validation mirrors the serializer so failures surface before the round-trip ──
  const problems = useMemo(() => {
    if (!current) return [];
    const out = [];
    if (!(current.name || '').trim()) out.push('Patient type needs a name.');
    if (!current.stages.length) out.push('Add at least one stage.');

    const names = current.stages.map(s => (s.name || '').trim().toLowerCase());
    if (names.some(n => !n)) out.push('Every stage needs a name.');
    if (new Set(names.filter(Boolean)).size !== names.filter(Boolean).length) {
      out.push('Stage names must be unique within this type.');
    }
    if (!current.stages.some(s => s.stage_kind === 'open')) {
      out.push('At least one stage must be "Open" — new enquiries need somewhere to land.');
    }
    if (current.stages.filter(s => s.is_default).length > 1) {
      out.push('Only one stage can be the default entry stage.');
    }
    const dupName = types?.some((t, i) =>
      i !== selected && !t._deleted &&
      (t.name || '').trim().toLowerCase() === (current.name || '').trim().toLowerCase());
    if (dupName) out.push('Another patient type already has this name.');
    return out;
  }, [current, types, selected]);

  const save = async () => {
    if (problems.length) { ToastMsg('error', problems[0]); return; }
    setSaving(true);
    try {
      const payload = {
        name: current.name.trim(),
        description: current.description || '',
        colour: current.colour || '',
        order: Number(current.order) || 0,
        is_active: current.is_active !== false,
        stages: current.stages.map((s, i) => ({
          ...(s.id ? { id: s.id } : {}),
          name: (s.name || '').trim(),
          stage_kind: s.stage_kind || 'open',
          maps_to_status: s.maps_to_status || 'new',
          order: i,
          is_default: !!s.is_default,
          colour: s.colour || '',
          is_active: s.is_active !== false,
        })),
      };
      if (current.code) payload.code = current.code;

      if (current._new) await apiCreate('engage/patient-types/', payload);
      else              await apiUpdate(`engage/patient-types/${current.id}/`, payload);

      ToastMsg('success', `Saved "${payload.name}" with ${payload.stages.length} stage(s)`);
      await load(selected);
    } catch (e) {
      const msg = e?.response?.data?.message || e?.response?.data?.stages
        || 'Save failed — check the stage list';
      ToastMsg('error', Array.isArray(msg) ? msg[0] : String(msg));
    } finally {
      setSaving(false);
    }
  };

  const deleteType = async () => {
    if (current._new) {
      setTypes(ts => ts.filter((_, i) => i !== selected));
      setSelected(0);
      return;
    }
    if (current.enquiry_count > 0) {
      ToastMsg('error',
        `"${current.name}" is used by ${current.enquiry_count} enquiry(s). Deactivate it instead.`);
      return;
    }
    if (!window.confirm(`Delete patient type "${current.name}" and its ${current.stages.length} stage(s)?`)) return;
    setSaving(true);
    try {
      await apiRemove(`engage/patient-types/${current.id}/`);
      ToastMsg('success', `Deleted "${current.name}"`);
      await load(0);
    } catch (e) {
      ToastMsg('error', e?.response?.data?.message || 'Delete failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <button onClick={() => navigate('/engage/dashboard')}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
        <ChevronLeft className="w-4 h-4" /> Back to Dashboard
      </button>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center text-white shadow-sm">
            <Tag className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Patient Types</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Each type carries its own stage ladder. Patient enquiries pick a type, then only that type&apos;s stages are selectable.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-[11px] text-amber-600 dark:text-amber-400 font-medium">Unsaved changes</span>}
          <button onClick={addType}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800/60 hover:bg-violet-100">
            <Plus className="w-3.5 h-3.5" /> Add patient type
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>
      )}

      {types === null ? (
        <div className="py-16 text-center text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin mx-auto mb-1" /> Loading…
        </div>
      ) : types.length === 0 ? (
        <div className="py-16 text-center text-gray-500 bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700">
          No patient types yet. Click <b>Add patient type</b> to create the first one.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
          {/* ── type list ── */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm h-fit">
            <div className="px-3 py-2 bg-gray-50 dark:bg-gray-900/40 text-[11px] uppercase tracking-wide text-gray-500 font-medium">
              {types.length} type{types.length !== 1 ? 's' : ''}
            </div>
            <ul className="divide-y divide-gray-100 dark:divide-gray-700">
              {types.map((t, i) => (
                <li key={t._key}>
                  <button onClick={() => setSelected(i)}
                    className={cn('w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors',
                      i === selected && 'bg-violet-50 dark:bg-violet-900/20 border-l-2 border-violet-500')}>
                    <div className="flex items-center justify-between gap-2">
                      <span className={cn('text-sm font-medium truncate',
                        t.is_active === false ? 'text-gray-400 line-through' : 'text-gray-900 dark:text-white')}>
                        {t.name || <i className="text-gray-400">Untitled</i>}
                      </span>
                      {t._new && <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">NEW</span>}
                    </div>
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {t.stages.length} stage{t.stages.length !== 1 ? 's' : ''}
                      {t.enquiry_count > 0 && ` · ${t.enquiry_count} enquiry`}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* ── editor ── */}
          {current && (
            <div className="space-y-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 p-4 shadow-sm space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Name">
                    <input value={current.name || ''} onChange={(e) => patchType({ name: e.target.value })}
                      placeholder="Out-Patient (OP)" className={inputCls} />
                  </Field>
                  <Field label="Code" hint="Stable key for integrations. Auto-derived from the name if left blank.">
                    <input value={current.code || ''} onChange={(e) => patchType({ code: e.target.value })}
                      placeholder="op" className={inputCls} disabled={!current._new} />
                  </Field>
                  <Field label="Description" className="sm:col-span-2">
                    <input value={current.description || ''} onChange={(e) => patchType({ description: e.target.value })}
                      placeholder="Walk-in and booked consultations." className={inputCls} />
                  </Field>
                  <Field label="Order">
                    <input type="number" min="0" value={current.order ?? 0}
                      onChange={(e) => patchType({ order: Number(e.target.value) || 0 })} className={inputCls} />
                  </Field>
                  <Field label="Active">
                    <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 pt-1.5">
                      <input type="checkbox" checked={current.is_active !== false}
                        onChange={(e) => patchType({ is_active: e.target.checked })}
                        className="rounded border-gray-300" />
                      Selectable on new enquiries
                    </label>
                  </Field>
                </div>
              </div>

              {problems.length > 0 && (
                <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[12px] text-amber-800 dark:text-amber-300">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                    <ul className="list-disc list-inside space-y-0.5">
                      {problems.map((p, i) => <li key={i}>{p}</li>)}
                    </ul>
                  </div>
                </div>
              )}

              {/* stage ladder */}
              <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm">
                <div className="px-4 py-2.5 bg-gray-50 dark:bg-gray-900/40 flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-wide text-gray-500 font-medium">
                    Stage ladder — {current.stages.length} stage{current.stages.length !== 1 ? 's' : ''}
                  </div>
                  <button onClick={addStage}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-900/20 hover:bg-violet-100">
                    <Plus className="w-3 h-3" /> Add stage
                  </button>
                </div>
                <table className="w-full text-sm">
                  <thead className="text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-100 dark:border-gray-700">
                    <tr>
                      <th className="px-2 py-2 w-16"></th>
                      <th className="px-3 py-2 text-left font-medium">Stage name</th>
                      <th className="px-3 py-2 text-left font-medium">Kind</th>
                      <th className="px-3 py-2 text-left font-medium">Reports as</th>
                      <th className="px-3 py-2 text-center font-medium w-20">Entry</th>
                      <th className="px-3 py-2 text-center font-medium w-16">Used</th>
                      <th className="px-3 py-2 w-10"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {current.stages.map((s, i) => (
                      <tr key={s._key} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                        <td className="px-2 py-2">
                          <div className="flex items-center gap-0.5 text-gray-400">
                            <GripVertical className="w-3 h-3" />
                            <button onClick={() => moveStage(i, -1)} disabled={i === 0}
                              className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-25">
                              <ArrowUp className="w-3 h-3" />
                            </button>
                            <button onClick={() => moveStage(i, 1)} disabled={i === current.stages.length - 1}
                              className="p-0.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-25">
                              <ArrowDown className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <input value={s.name || ''} onChange={(e) => patchStage(i, { name: e.target.value })}
                            placeholder="Appointment Booked" className={cellCls} />
                        </td>
                        <td className="px-3 py-2">
                          <select value={s.stage_kind || 'open'}
                            onChange={(e) => patchStage(i, { stage_kind: e.target.value })}
                            className={cn(cellCls, 'font-medium', KIND_BADGE[s.stage_kind])}>
                            {STAGE_KINDS.map(k => <option key={k.value} value={k.value}>{k.label}</option>)}
                          </select>
                        </td>
                        <td className="px-3 py-2">
                          <select value={s.maps_to_status || 'new'}
                            onChange={(e) => patchStage(i, { maps_to_status: e.target.value })}
                            className={cellCls}>
                            {ENGAGE_STATUS_ORDER.map(k => (
                              <option key={k} value={k}>{ENGAGE_STATUS_LABEL[k]}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button onClick={() => setDefaultStage(i)} title="Entry stage for new enquiries"
                            className={cn('p-1 rounded', s.is_default
                              ? 'text-amber-500 bg-amber-50 dark:bg-amber-900/20'
                              : 'text-gray-300 hover:text-amber-400')}>
                            <Star className={cn('w-3.5 h-3.5', s.is_default && 'fill-current')} />
                          </button>
                        </td>
                        <td className="px-3 py-2 text-center text-[11px] text-gray-500 tabular-nums">
                          {s.enquiry_count ?? 0}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button onClick={() => removeStage(i)}
                            disabled={s.enquiry_count > 0}
                            title={s.enquiry_count > 0 ? 'Stage has enquiries on it' : 'Remove stage'}
                            className="p-1 rounded text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-25 disabled:hover:text-gray-400 disabled:hover:bg-transparent">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between gap-2 flex-wrap">
                <button onClick={deleteType} disabled={saving}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/60 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50">
                  <Trash2 className="w-3.5 h-3.5" /> Delete type
                </button>
                <button onClick={save} disabled={saving || problems.length > 0}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm text-white bg-gradient-to-r from-violet-500 to-fuchsia-600 hover:from-violet-600 hover:to-fuchsia-700 disabled:opacity-50">
                  <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save patient type'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="p-3 rounded-lg bg-violet-50 dark:bg-violet-900/10 border border-violet-100 dark:border-violet-800/40 text-[11px] text-violet-800 dark:text-violet-300 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <p>
          <b>Reports as</b> is the bridge to reporting. Every engage report — the enquiry funnel, the dashboard,
          conversion analytics — groups by the fixed enquiry status list, not by your custom stages. Each stage declares
          which of those buckets it rolls up to, so you can add as many stages as you like without any report losing
          track of the enquiry. The <Star className="w-3 h-3 inline -mt-0.5 fill-current text-amber-500" /> stage is
          where a new enquiry of this type starts. A stage that already has enquiries on it cannot be deleted.
        </p>
      </div>
    </div>
  );
}

const inputCls = 'w-full px-2.5 py-1.5 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-violet-500 disabled:opacity-60';
const cellCls  = 'w-full px-2 py-1 text-sm rounded bg-transparent border border-transparent hover:border-gray-200 dark:hover:border-gray-600 focus:border-violet-500 focus:bg-white dark:focus:bg-gray-900 focus:outline-none text-gray-900 dark:text-white';

function Field({ label, hint, className, children }) {
  return (
    <div className={className}>
      <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-medium mb-1">{label}</label>
      {children}
      {hint && <p className="text-[10px] text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}
